import React, { useMemo, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';

import { useIntl, FormattedMessage } from 'react-intl';

import {
    CalculatorStepPanel,
    MessagePropType,
    CalculationPanel,
    CalculationLineBlock,
    useCalculator,
    MessageBanner,
    MessageBannerVariants,
    MessageBannerCTAVariants,
    Sizes,
    useForm,
    CircleSpinner,
    trackHeapEvent,
} from '@ratehub/base-ui';

import getPenaltyCalculations from '../functions/getPenaltyCalculations';


function PenaltyStepSummary({
    stepNumber,
    title,

    messageBannerCopy,
    messageBannerLinkText,
    messageBannerLinkHREF,
    ...otherProps
}) {
    const form = useForm();
    const intl = useIntl();
    const calculator = useCalculator();

    const { calculatedPenaltyFeeObject } = form.values;

    // REQUIREMENT: we want to track the # of times this happens (and look for patterns in values)
    // NOTE: spreading the values converts from getters to standard JS values.
    useEffect(() => reaction(
        () => form.values.calculatedPenaltyFeeObjectError,
        error => {
            if (error) {
                trackHeapEvent('Penalty Calculator Failed Calculate Penalty', { ...form.values });
            }
        },
    ), [ form ]);

    const formulas = useMemo(() => (
        getFormulas(intl.locale, calculatedPenaltyFeeObject)
    ), [ intl.locale, calculatedPenaltyFeeObject ]);

    const shouldShowSuccessMessageBanner = messageBannerCopy && messageBannerLinkText && messageBannerLinkHREF;

    const isVisible = calculator.hasCompletedPreviousStep(stepNumber);

    return (
        <PenaltyCalculationSummary
            stepNumber={stepNumber}
            isVisible={isVisible}
            title={title}
            {...otherProps}
        >
            <Choose>
                <When condition={form.values.calculatedPenaltyFeeObjectIsFetching}>
                    <CircleSpinner />
                </When>

                <When condition={form.values.calculatedPenaltyFeeObjectError}>
                    <MessageBanner
                        className="messageBanner"
                        message={(
                            <FormattedMessage
                                id="mtg-calculators.penaltyCalculator.fetchError"
                                defaultMessage="We are unable to calculate your penalty based on the information provided.
                                    For the most accurate penalty information, please contact your lender directly."
                            />
                        )}
                        iconKey="exclamation-mark"
                        variant="error"
                    />
                </When>

                <Otherwise>
                    <CalculationPanel className="calcPanel">
                        <CalculationLineBlock
                            formulas={formulas}
                        />
                    </CalculationPanel>

                    <If condition={shouldShowSuccessMessageBanner}>
                        <MessageBanner
                            className="messageBanner"
                            message={messageBannerCopy}
                            variant={MessageBannerVariants.SUCCESS}
                            iconKey="light-bulb"
                            ctaMessage={messageBannerLinkText}
                            href={messageBannerLinkHREF}
                            ctaVariant={MessageBannerCTAVariants.PRIMARY_ANCHOR}
                        />
                    </If>

                    <p className="footnotes rh-text-s">
                        <FormattedMessage
                            id="mtg-calculators.penaltyCalculator.footnotes"
                            defaultMessage={
                                `*Disclaimer: Please note that the calculation results are estimates based on our most up-to-date
                                information sourced from lenders’ publicly stated methodology and first-hand accounts.
                                This information is subject to change and do not include special offers or any discharges,
                                registration, reinvestment or transfer fees. For more details, contact your lender directly.`
                            }
                        />
                    </p>
                </Otherwise>
            </Choose>
        </PenaltyCalculationSummary>
    );
}

function getFormulas(locale, calculatedPenaltyFeeObject) {
    const calculations = getPenaltyCalculations({
        locale: locale,
        balanceName : 'currentBalance',
        rateName : 'currentRate',
        rateTypeName : 'rateType',
        originalTermName : 'originalTerm',
        startDateName : 'startDate',
        finalFeeName : 'penaltyFee',
        irdFactorName : 'irdFactor',
    });

    if (calculatedPenaltyFeeObject?.ird?.penalty > calculatedPenaltyFeeObject?.threeMonthPenalty) {
        return [
            calculations.balanceCalculation,
            calculations.irdFactorCalculation,
            calculations.monthsRemainingCalculation,
            calculations.finalPenaltyFeeCalculation,
        ];
    }

    return [
        calculations.balanceCalculation,
        calculations.rateCalculation,
        calculations.threeMonthsFactorCalculation,
        calculations.finalPenaltyFeeCalculation,
    ];
}

PenaltyStepSummary.propTypes = {
    stepNumber: PropTypes.number.isRequired,
    title: MessagePropType.isRequired,

    messageBannerCopy: PropTypes.string,
    messageBannerLinkText: PropTypes.string,
    messageBannerLinkHREF: PropTypes.string,
};

PenaltyStepSummary.defaultProps = {
    messageBannerCopy: undefined,
    messageBannerLinkText: undefined,
    messageBannerLinkHREF: undefined,
};

const PenaltyCalculationSummary = styled(CalculatorStepPanel)`
    > .calcPanel,
    > .messageBanner {
        margin-bottom: ${Sizes.SPACING.TWO};
    }

    > .footnotes {
        margin: auto;
        padding-bottom: ${Sizes.SPACING.TWO};
    }
`;

export default observer(PenaltyStepSummary);
